import api from '../../../services/api';

// Interfaces para a API da UTalk
interface Contact {
  name: string;
  phoneNumber: string;
  variables?: Record<string, any>;
}

interface BulkMessageRequest {
  channelId: string;
  name: string;
  contacts: Contact[];
  templateMessageId: string;
  scheduledTo?: string;
  tags?: string[];
}

interface BulkMessageResponse {
  success: boolean;
  data?: any;
  error?: string;
}

class UtalkService {
  private readonly baseURL = process.env.REACT_APP_UTALK_API_BASE_URL || 'https://app-utalk.umbler.com/api';
  private readonly apiKey = process.env.REACT_APP_UTALK_API_KEY;

  /**
   * Envia mensagens em massa usando a API da UTalk
   */
  async sendBulkMessage(request: BulkMessageRequest): Promise<BulkMessageResponse> {
    try {
      if (!this.apiKey) {
        throw new Error('UTALK_API_KEY não configurada nas variáveis de ambiente');
      }

      // Validar campos obrigatórios
      if (!request.channelId || !request.name || !request.contacts || !request.templateMessageId) {
        throw new Error('Campos obrigatórios ausentes: channelId, name, contacts, templateMessageId');
      }

      // Validar se há pelo menos um contato
      if (request.contacts.length === 0) {
        throw new Error('Pelo menos um contato deve ser fornecido');
      }

      // Validar formato dos contatos
      for (const contact of request.contacts) {
        if (!contact.name || !contact.phoneNumber) {
          throw new Error('Todos os contatos devem ter nome e telefone');
        }
      }

      const response = await fetch(`${this.baseURL}/v1/bulk-send-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelId: request.channelId,
          name: request.name,
          contacts: request.contacts,
          templateMessageId: request.templateMessageId,
          scheduledTo: request.scheduledTo,
          tags: request.tags
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Erro na API: ${response.status}`);
      }

      return {
        success: true,
        data: data
      };

    } catch (error: any) {
      console.error('Erro ao enviar mensagem em massa:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao enviar mensagem em massa'
      };
    }
  }

  /**
   * Valida se a configuração da API está correta
   */
  validateConfiguration(): { isValid: boolean; message: string } {
    if (!this.apiKey) {
      return {
        isValid: false,
        message: 'UTALK_API_KEY não configurada nas variáveis de ambiente'
      };
    }

    if (!this.baseURL) {
      return {
        isValid: false,
        message: 'UTALK_API_BASE_URL não configurada nas variáveis de ambiente'
      };
    }

    return {
      isValid: true,
      message: 'Configuração válida'
    };
  }

  /**
   * Formatar número de telefone para o formato da UTalk (+5511999999999)
   */
  formatPhoneNumber(phone: string): string {
    // Remove todos os caracteres não numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Se já tem código do país, retorna como está
    if (cleaned.startsWith('55')) {
      return `+${cleaned}`;
    }
    
    // Adiciona código do Brasil (55) se não tiver
    return `+55${cleaned}`;
  }

  /**
   * Validar número de telefone brasileiro
   */
  validatePhoneNumber(phone: string): { isValid: boolean; message: string } {
    const cleaned = phone.replace(/\D/g, '');
    
    // Deve ter 10 ou 11 dígitos (com DDD)
    if (cleaned.length < 10 || cleaned.length > 11) {
      return {
        isValid: false,
        message: 'Telefone deve ter 10 ou 11 dígitos (com DDD)'
      };
    }

    // DDD deve estar entre 11 e 99
    const ddd = parseInt(cleaned.slice(0, 2));
    if (ddd < 11 || ddd > 99) {
      return {
        isValid: false,
        message: 'DDD inválido'
      };
    }

    return {
      isValid: true,
      message: 'Telefone válido'
    };
  }
}

export const utalkService = new UtalkService();
export default utalkService;
